import React from "react";
import { Crown, Sparkles, ShieldCheck, Receipt } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * PaymentSummaryDialog — pre-checkout transparency screen.
 *
 * Indian SaaS convention is to show the tax breakdown BEFORE the payment
 * gateway modal opens, so the user knows exactly what they're paying and
 * why. Razorpay's own modal only shows a single "amount" figure — that's
 * why we surface subtotal + GST here first.
 *
 * All amounts are pre-computed server-side (`/razorpay/config` &
 * `/razorpay/create-order` both return authoritative breakdowns) so this
 * component never does math — it just renders what the backend hands it.
 *
 * Props:
 *   open        boolean — controlled visibility
 *   onCancel    () => void — user closed / dismissed
 *   onConfirm   () => void — user pressed "Proceed to Pay"; caller should
 *                            open the Razorpay modal
 *   plan        "featured" | "premium"
 *   pricing     { base_inr, gst_rate, gst_inr, total_inr }  (from API)
 *   loading     boolean — disables the CTA while checkout order is being
 *                         created / Razorpay script is loading
 */

function inr(v) {
  // Pretty-print a rupee amount with 2-decimal precision when needed.
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  return n.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

export default function PaymentSummaryDialog({ open, onCancel, onConfirm, plan, pricing, loading }) {
  const isPremium = plan === "premium";
  const gstPercent = pricing?.gst_rate != null ? Math.round(pricing.gst_rate * 100) : 18;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !loading) onCancel?.(); }}>
      <DialogContent
        className="bg-white sm:max-w-md"
        data-testid="payment-summary-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
            <Receipt size={18} className="text-[#7A5A22]" /> Payment Summary
          </DialogTitle>
          <DialogDescription>
            Review the amount before you proceed to secure payment.
          </DialogDescription>
        </DialogHeader>

        {/* Plan header */}
        <div className={`rounded-2xl px-4 py-4 border ${isPremium ? "bg-[#F5EFE6] border-[#C9A86A]" : "bg-[#EEF2FF] border-[#93C5FD]"}`}>
          <div className="flex items-center gap-2">
            {isPremium ? <Crown size={16} className="text-[#7A5A22]" /> : <Sparkles size={16} className="text-[#1E3A8A]" />}
            <span className={`font-medium ${isPremium ? "text-[#7A5A22]" : "text-[#1E3A8A]"}`} data-testid="summary-plan-name">
              {isPremium ? "Premium" : "Featured"} Plan
            </span>
            <span className="ml-auto text-xs text-[#4B5563]">30 days</span>
          </div>
          <div className="text-xs text-[#4B5563] mt-1">
            Full plan benefits activate instantly on successful payment.
          </div>
        </div>

        {/* Line items */}
        <div className="rounded-2xl border border-[#E7DDCF] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#F5EFE6]">
            <span className="text-sm text-[#4B5563]">Subtotal</span>
            <span className="text-sm text-[#111827] font-medium" data-testid="summary-subtotal">
              {inr(pricing?.base_inr)}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#F5EFE6]">
            <span className="text-sm text-[#4B5563]">
              GST <span className="text-[#7A5A22]">({gstPercent}%)</span>
            </span>
            <span className="text-sm text-[#111827] font-medium" data-testid="summary-gst">
              {inr(pricing?.gst_inr)}
            </span>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-[#FBF7F0]">
            <span className="font-heading text-lg text-[#111827]">Total Payable</span>
            <span className="font-heading text-xl text-[#1F4E3D]" data-testid="summary-total">
              {inr(pricing?.total_inr)}
            </span>
          </div>
        </div>

        {/* Trust signals */}
        <div className="flex items-center gap-2 text-xs text-[#4B5563]">
          <ShieldCheck size={13} className="text-[#1F4E3D]" />
          Secure payment powered by Razorpay · UPI · Cards · Netbanking · Wallets
        </div>

        <DialogFooter className="mt-2">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            data-testid="payment-summary-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading || !pricing}
            className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-2"
            data-testid="payment-summary-proceed"
          >
            {loading ? "Opening…" : (
              <>Proceed to Pay {inr(pricing?.total_inr)}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
